import React, { useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'

import BoardContainer from './BoardContainer'
import QueueContainer from './Queue/QueueContainer'
import Content from 'common/components/Content'
import DiscussBar from '../Discuss/DiscussBar'
import { RowSpacer } from 'common/components/Row'
import Toolbar from '../Toolbar'
import ModeSwitcher from '../MainToolbar/ModeSwitcher'
import BoardTitle from '../MainToolbar/BoardTitle/BoardTitle'
import ViewControls from '../MainToolbar/ViewControls'
import EditControls from '../MainToolbar/EditControls'
import DiscussControls from '../MainToolbar/DiscussControls'
import useToggle from 'common/hooks/useToggle'
import SingleBlock from './SingleBlock'

function useToolbarHide(boardId, toolsCanHide) {
  const hideDelay = 3
  const [hide, toggleHide] = useToggle(false)

  const showTools = useCallback(() => {
    hideTimeout.current && clearTimeout(hideTimeout.current)
    toggleHide(false)
  }, [toggleHide])

  const toolsHidden = toolsCanHide && hide
  const hideTimeout = useRef()
  const hideTools = useCallback(() => {
    if (toolsHidden) return
    hideTimeout.current = setTimeout(() => {
      toggleHide(true)
    }, hideDelay * 1000)
  }, [toolsHidden, toggleHide])

  // hide on load, clear timeout on unload
  useEffect(() => {
    toggleHide(false)
    hideTools()
    return () => {
      hideTimeout.current && clearTimeout(hideTimeout.current)
    }
  }, [toggleHide, boardId])

  return { toolsHidden, toggleHide, showTools, hideTools }
}

export const BoardView = ({
  board,
  currentBlock,
  mode,
  smallScreen,
  toolsHidden: toolsHidden1,
  toolsCanHide
}) => {
  const { toolsHidden: toolsHidden2, showTools, hideTools } = useToolbarHide(
    board.id,
    toolsCanHide
  )

  const toolsHidden = toolsHidden1 || toolsHidden2

  const handleMouseOver = useCallback(
    e => {
      if (e.movementX || e.movementY) showTools()
    },
    [showTools]
  )

  return (
    <Content>
      <Toolbar
        empty={toolsHidden}
        solid={toolsCanHide}
        onClick={toolsHidden ? showTools : undefined}
        onMouseMove={handleMouseOver}
        onMouseOut={hideTools}
      >
        <BoardTitle minimal={toolsHidden} />

        {!toolsHidden && (
          <>
            <RowSpacer />

            {mode === 'view' ? (
              <ViewControls />
            ) : mode === 'edit' ? (
              <EditControls />
            ) : mode === 'discuss' ? (
              !smallScreen && <DiscussControls />
            ) : null}

            <ModeSwitcher />
          </>
        )}
      </Toolbar>

      <BoardContainer board={board} hideTools={hideTools} />
      {currentBlock && <SingleBlock block={currentBlock} board={board} />}

      {mode === 'edit' && <QueueContainer />}

      {/* Discuss Bar */}
      {mode === 'discuss' && <DiscussBar />}
    </Content>
  )
}

BoardView.propTypes = {
  board: PropTypes.object,
  mode: PropTypes.string.isRequired
}

export default BoardView
